import { useMemo } from 'react'
import { THEATER_CONFIGS, coordKey } from '@fleetduel/shared'
import type { TerrainCell, TerrainType } from '@fleetduel/shared'
import { useTheaterStore } from '../store/theaterStore.js'
import { THEATER_PALETTES_2D } from '../styles/theaterPalettes.js'
import type { TheaterPalette2D } from '../styles/theaterPalettes.js'

function toTerrainMap(layout: TerrainCell[], enabled: boolean): Map<string, TerrainType> {
  const map = new Map<string, TerrainType>()
  if (enabled) {
    for (const t of layout) map.set(coordKey(t.coord), t.type)
  }
  return map
}

/**
 * Resolved theater context for the current game:
 * per-board terrain maps, blocked-cell keys and 2D palette overrides.
 *
 * Layouts are randomized server-side per game/per board (sent with
 * room_joined); the static config is the fallback before a room exists.
 */
export function useTheater() {
  const theaterId = useTheaterStore(s => s.theaterId)
  const terrainEnabled = useTheaterStore(s => s.terrainEnabled)
  const ownTerrain = useTheaterStore(s => s.ownTerrain)
  const enemyTerrain = useTheaterStore(s => s.enemyTerrain)

  const config = THEATER_CONFIGS[theaterId]

  // Own board (also what the Placement screen uses)
  const terrainCells = useMemo(
    () => toTerrainMap(ownTerrain ?? config.terrainCells, terrainEnabled),
    [ownTerrain, config, terrainEnabled],
  )
  const blockedCells = useMemo(() => new Set(terrainCells.keys()), [terrainCells])

  // Enemy board
  const enemyTerrainCells = useMemo(
    () => toTerrainMap(enemyTerrain ?? config.terrainCells, terrainEnabled),
    [enemyTerrain, config, terrainEnabled],
  )
  const enemyBlockedCells = useMemo(() => new Set(enemyTerrainCells.keys()), [enemyTerrainCells])

  const palette: TheaterPalette2D = THEATER_PALETTES_2D[theaterId]

  return {
    theaterId,
    config,
    terrainEnabled,
    terrainCells,
    blockedCells,
    enemyTerrainCells,
    enemyBlockedCells,
    palette,
  }
}
